import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, filter } from 'rxjs';
import {
  BRAND_CONFIGS,
  BrandConfig,
  BrandKey,
  DEFAULT_BRAND_KEY,
  resolveBrandKey,
} from '../shared/constants/brand.constants';

@Injectable({
  providedIn: 'root',
})
export class BrandService {
  private readonly brandSubject = new BehaviorSubject<BrandConfig>(
    BRAND_CONFIGS[DEFAULT_BRAND_KEY]
  );

  readonly brand$ = this.brandSubject.asObservable();

  constructor(
    private router: Router,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.resolveAndApplyBrand();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.resolveAndApplyBrand());
  }

  getCurrentBrandConfig(): BrandConfig {
    return this.brandSubject.value;
  }

  getCurrentBrandKey(): BrandKey {
    return this.brandSubject.value.key;
  }

  private resolveAndApplyBrand(): void {
    const hostname = window.location.hostname;
    const queryBrand = new URLSearchParams(window.location.search).get('brand');
    const brandKey = resolveBrandKey(hostname, queryBrand);
    const brandConfig = BRAND_CONFIGS[brandKey];

    this.brandSubject.next(brandConfig);
    this.applyBrandTheme(brandConfig);
  }

  private applyBrandTheme(brandConfig: BrandConfig): void {
    const rootElement = this.document.documentElement;
    rootElement.setAttribute('data-brand', brandConfig.key);

    rootElement.style.setProperty('--brand-primary', brandConfig.colors.primary);
    rootElement.style.setProperty(
      '--brand-secondary',
      brandConfig.colors.secondary
    );
    rootElement.style.setProperty('--brand-accent', brandConfig.colors.accent);
    rootElement.style.setProperty(
      '--brand-text-on-dark',
      brandConfig.colors.textOnDark
    );
    rootElement.style.setProperty(
      '--brand-dark-background',
      brandConfig.colors.darkBackground
    );

    this.applyFavicon(brandConfig.key);
  }

  private applyFavicon(brandKey: BrandKey): void {
    const faviconMap: Partial<Record<BrandKey, string>> = {
      armorvue: 'assets/armorvue-favicon-75x75.png',
      newsouth: 'assets/newsouth-favicon-100x100.png',
    };

    const href = faviconMap[brandKey] ?? 'favicon.ico';
    const type = href.endsWith('.ico') ? 'image/x-icon' : 'image/png';

    let link = this.document.querySelector<HTMLLinkElement>("link[rel='icon']");
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'icon');
      this.document.head.appendChild(link);
    }
    link.setAttribute('type', type);
    link.setAttribute('href', href);
  }
}
